import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import type { ToolbarReviewExperience } from '../experiences/toolbar-review-experience';
import { thinkingPluginKey } from '../pm-plugins/thinking-plugin';
import {
	addEntryPointClickedAnalytics,
	addRetryThinkingAnalytics,
	fireAiInteractionInitiatedUnifiedAnalytics,
} from '../ui/utils/analytics';
import type { SetThinkingStateAction } from './types';

/**
 * Single editor-action for every thinking transition: sets the plugin status.
 * When moving to `thinking` it also fires the relevant analytics:
 * - `entryPoint` → entry-point + unified aiInteraction analytics (start).
 * - `isRetry`    → the error-screen retry-button analytics (retry).
 * When moving to `error`, `template` selects the error-screen copy/links.
 */
export const setThinkingStateAction =
	(
		api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
		reviewExperience?: ToolbarReviewExperience,
	): SetThinkingStateAction =>
	(state, options): boolean => {
		if (
			state === 'thinking' &&
			api?.aiSuggestions?.sharedState.currentState()?.thinkingStatus !== 'thinking'
		) {
			// Start before executing the command so a rejected or thrown dispatch is counted.
			reviewExperience?.start(options);
		}
		try {
			const dispatched =
				api?.core?.actions.execute(({ tr }) => {
					if (state === 'thinking') {
						if (options?.entryPoint) {
							// TODO: EDITOR-8269 - placeholder id; may come from the backend once the
							// unified suggested-edits analytics structure is finalised.
							const singleInstrumentationId = crypto.randomUUID();
							addEntryPointClickedAnalytics(api?.analytics?.actions)(tr, options.entryPoint);
							fireAiInteractionInitiatedUnifiedAnalytics(
								api?.analytics?.actions,
								options.entryPoint,
								singleInstrumentationId,
							);
						}
						if (options?.isRetry) {
							addRetryThinkingAnalytics(api?.analytics?.actions)(tr);
						}
					}
					tr.setMeta(thinkingPluginKey, {
						status: state,
						...(state === 'error' && options?.template
							? { error: { template: options.template } }
							: {}),
					});
					return tr;
				}) || false;
			if (!dispatched) {
				reviewExperience?.failure('dispatchFailed');
			}
			return dispatched;
		} catch (error) {
			reviewExperience?.failure('dispatchError');
			throw error;
		}
	};
